/*
 * Light or dark, for THIS browser.
 *
 * The stylesheet declares every colour once, both sides in one light-dark(), and
 * the browser picks the side from <html>'s color-scheme. With no choice made that
 * is `light dark` - the operating system's setting, which is what the site has
 * always followed and still does by default. The header's switch pins one side
 * by putting data-color-scheme="light" | "dark" on <html>, and a rule on that
 * attribute narrows color-scheme to the side named.
 *
 * WHY THIS IS NOT A SITE SETTING. The theme (Amber, Plum, ...) is the site's
 * face: one row in kb.SiteSettings, applied for every reader. Light or dark is a
 * property of the reader's eyes, screen and room - the same person may want each
 * on a different machine - so it is remembered in localStorage, per browser, and
 * never sent anywhere. The pattern is the theme memory in SiteBranding.tsx:
 * applied in main.tsx before the first paint, so a reader who pinned dark does
 * not see a white flash, and read by the switch so its position matches what
 * the page is showing.
 *
 * TWO POSITIONS, NOT THREE. Until the reader clicks, nothing is stored, the
 * attribute is absent and the browser's setting decides - including a change to
 * it while the page is open, which the media query reports and the switch
 * follows. A click pins that side for this browser. There is no third "follow
 * the browser again" position: the switch was asked for as light or dark, and
 * a reader who wants the automatic behaviour back clears the site's data.
 */

import { useCallback, useEffect, useState } from 'react';

export type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'kb.colorScheme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isColorScheme(value: unknown): value is ColorScheme {
  return value === 'light' || value === 'dark';
}

/** The remembered choice, or null when none was made or storage is unavailable. */
export function readRememberedColorScheme(): ColorScheme | null {
  try {
    const remembered = localStorage.getItem(STORAGE_KEY);
    return isColorScheme(remembered) ? remembered : null;
  } catch {
    return null;
  }
}

/**
 * Put the remembered choice on <html> before the first render. Called once from
 * main.tsx, beside applyRememberedTheme. A browser with nothing stored, or with
 * no storage at all, is left to its own setting.
 */
export function applyRememberedColorScheme(): void {
  const remembered = readRememberedColorScheme();
  if (remembered) document.documentElement.dataset.colorScheme = remembered;
}

/** What the browser would pick on its own. jsdom has no matchMedia, hence the guard. */
function browserPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export interface ColorSchemeState {
  /** The side the page is showing: the choice if one was made, else the browser's. */
  scheme: ColorScheme;
  /** True once the reader has clicked; false while the browser's setting decides. */
  chosen: boolean;
  choose: (next: ColorScheme) => void;
}

export function useColorScheme(): ColorSchemeState {
  const [choice, setChoice] = useState<ColorScheme | null>(readRememberedColorScheme);
  const [browserDark, setBrowserDark] = useState<boolean>(browserPrefersDark);

  // While nothing is chosen the switch follows the operating system, live: a
  // machine that goes dark at sunset moves the switch with the page.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setBrowserDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // The attribute IS the choice: present when one was made, absent otherwise.
  useEffect(() => {
    if (choice) document.documentElement.dataset.colorScheme = choice;
    else delete document.documentElement.dataset.colorScheme;
  }, [choice]);

  const choose = useCallback((next: ColorScheme) => {
    setChoice(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // No storage: the choice holds for this page and is forgotten with it.
    }
  }, []);

  return {
    scheme: choice ?? (browserDark ? 'dark' : 'light'),
    chosen: choice !== null,
    choose,
  };
}
